import { useEffect, useRef, useState } from 'react';
import { debounce } from 'core/common/utils/debounce';

const minScrollValue = 0;
const maxScrollValue = 100;
const debounceTimeMs = 100;

type Params = {
  timeoutMs?: number;
  thresholds?: Array<number>;
  onReachThreshold?: (value: number) => void;
};

export const useScrollProgress = (options?: Params) => {
  const { timeoutMs, thresholds, onReachThreshold } = options || {};

  const reachedThresholds = useRef<Set<number>>(new Set());

  const [percentage, setPercentage] = useState(minScrollValue);

  useEffect(() => {
    let mounted = true;

    const container = document.scrollingElement;

    const handleScroll = debounce(() => {
      if (!mounted || !container) return;

      const { scrollTop, scrollHeight, clientHeight } = container;

      const getProgress = (scrollPos: number, scrollSize: number, clientSize: number) =>
        +((scrollPos / (scrollSize - clientSize)) * maxScrollValue).toFixed(2);

      const verticalProgress = getProgress(scrollTop, scrollHeight, clientHeight);

      const value = isNaN(verticalProgress) ? minScrollValue : verticalProgress;

      setPercentage(value);

      if (!thresholds || !onReachThreshold) return;

      const currentVertical = Math.floor(value);

      thresholds.forEach((threshold) => {
        const isThresholdReached =
          currentVertical >= threshold && !reachedThresholds.current.has(threshold);

        if (!isThresholdReached) return;

        reachedThresholds.current.add(threshold);
        onReachThreshold(threshold);
      });
    }, timeoutMs ?? debounceTimeMs);

    document.addEventListener('scroll', handleScroll);

    return () => {
      mounted = false;
      document.removeEventListener('scroll', handleScroll);
    };
  }, [timeoutMs, thresholds, onReachThreshold]);

  return { percentage };
};
